import {
  ApiCreateCreditProductPayload,
  ApiCreditProductAdmin,
  CreditRewardCategory,
} from "griptonite-api-client/types";
import { z } from "zod";

import { Media } from "../forms/MediaInput";
import { REWARD_CATEGORIES } from "./rewardCategories";

// Category and cost start out empty on a new reward, so both are nullable here and checked as
// required at the end. The image is a preset's media or an upload; an upload has no uuid until it
// finishes.
export const RewardFormSchema = z
  .object({
    name: z.string().trim().min(1, { message: "Name required" }),
    description: z.string(),
    category: z
      .enum(REWARD_CATEGORIES as [CreditRewardCategory, ...CreditRewardCategory[]])
      .nullable(),
    image: z.custom<Media | null>().superRefine((value, ctx) => {
      if (!value) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Choose an image or upload one" });
      } else if (!("uuid" in value)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Wait for the image to upload" });
      }
    }),
    cost: z.number().int().min(1, { message: "Costs at least 1 credit" }).nullable(),
    isActive: z.boolean(),
    venues: z.array(z.string()).min(1, { message: "Choose at least one gym" }),
  })
  .superRefine((values, ctx) => {
    if (values.category === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Choose a category",
        path: ["category"],
      });
    }
    if (values.cost === null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Cost required", path: ["cost"] });
    }
  });

export type RewardFormValues = z.infer<typeof RewardFormSchema>;

// A new reward is enabled and offered at the gym it's created from.
export const rewardToFormValues = (
  reward: ApiCreditProductAdmin | undefined,
  venueUuid: string,
): RewardFormValues => ({
  name: reward?.name ?? "",
  description: reward?.description ?? "",
  category: reward?.category ?? null,
  image: reward?.image ?? null,
  cost: reward?.cost ?? null,
  isActive: reward?.isActive ?? true,
  venues: reward?.venues ?? [venueUuid],
});

// Only called with values that passed the schema, which requires category and cost.
export const rewardFormValuesToPayload = (
  values: RewardFormValues,
): ApiCreateCreditProductPayload => ({
  name: values.name,
  description: values.description,
  category: values.category!,
  image: values.image && "uuid" in values.image ? values.image.uuid : null,
  cost: values.cost!,
  isActive: values.isActive,
  venues: values.venues,
});
